import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, FileText, Mail, Phone } from "lucide-react";
import { DonorActivityLog } from "@/components/collective/DonorActivityLog";
import { DonorImpactReport } from "@/components/collective/DonorImpactReport";
import { useState } from "react";

export default function DonorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showImpactReport, setShowImpactReport] = useState(false);

  const { data: donor, isLoading } = useQuery({
    queryKey: ["donor-profile", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("collective_donors")
        .select(`
          *,
          collective_contributions (
            *,
            collective_contribution_allocations (
              *,
              deals (
                id,
                title,
                amount,
                athlete_id,
                athletes (
                  name,
                  sport,
                  year
                )
              )
            )
          ),
          collective_donor_activities (*)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  const getTierColor = (tier: string) => {
    const colors = {
      platinum: "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      gold: "bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100",
      silver: "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100",
      bronze: "bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-100",
      supporter: "bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100",
    };
    return colors[tier as keyof typeof colors] || "bg-secondary";
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">Loading donor profile...</div>
      </DashboardLayout>
    );
  }

  if (!donor) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">Donor not found</div>
      </DashboardLayout>
    );
  }

  const contributions = donor.collective_contributions || [];
  const avgGift = contributions.length > 0
    ? donor.total_lifetime_contributions / contributions.length
    : 0;

  // Get unique funded deals
  const fundedDeals = contributions
    .flatMap(c => c.collective_contribution_allocations || [])
    .filter(a => a.deals)
    .map(a => a.deals);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/collective/donors")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{donor.name}</h1>
              <Badge className={getTierColor(donor.tier)}>{donor.tier}</Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-muted-foreground">
              {donor.email && (
                <div className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  <span>{donor.email}</span>
                </div>
              )}
              {donor.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  <span>{donor.phone}</span>
                </div>
              )}
            </div>
          </div>
          <Button onClick={() => setShowImpactReport(true)}>
            <FileText className="mr-2 h-4 w-4" />
            Generate Impact Report
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Lifetime Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(donor.total_lifetime_contributions)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                This Year
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(donor.total_ytd_contributions)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Contributions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{contributions.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Average Gift
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(avgGift)}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Contribution History</CardTitle>
          </CardHeader>
          <CardContent>
            {contributions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No contributions recorded yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tax Deductible</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contributions.map((contribution) => (
                    <TableRow key={contribution.id}>
                      <TableCell>{formatDate(contribution.contribution_date)}</TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(contribution.amount)}
                      </TableCell>
                      <TableCell className="capitalize">{contribution.payment_method}</TableCell>
                      <TableCell>
                        <Badge variant={contribution.payment_status === 'completed' ? 'default' : 'secondary'}>
                          {contribution.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {contribution.is_tax_deductible ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {fundedDeals.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Funded Athletes & Deals</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fundedDeals.map((deal: any) => (
                    <TableRow
                      key={deal.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/deals/${deal.id}`)}
                    >
                      <TableCell className="font-medium">{deal.athletes?.name}</TableCell>
                      <TableCell>{deal.athletes?.sport}</TableCell>
                      <TableCell>{deal.athletes?.year}</TableCell>
                      <TableCell>{deal.title}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(deal.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <DonorActivityLog donorId={id!} activities={donor.collective_donor_activities || []} />
      </div>

      <DonorImpactReport
        open={showImpactReport}
        onOpenChange={setShowImpactReport}
        donor={donor}
        contributions={contributions}
        fundedDeals={fundedDeals}
      />
    </DashboardLayout>
  );
}
